import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Activity, 
  ArrowLeft, 
  RotateCcw, 
  User, 
  Heart, 
  TrendingUp, 
  CheckCircle2, 
  AlertTriangle 
} from 'lucide-react';

export const BMICalculator = () => {
  const { withAd, toast } = useAdRedirect();
  const [unitSystem, setUnitSystem] = useState('metric'); // 'metric' | 'imperial'
  const [weightKg, setWeightKg] = useState('70');
  const [heightCm, setHeightCm] = useState('175');
  const [weightLbs, setWeightLbs] = useState('154');
  const [heightFt, setHeightFt] = useState('5');
  const [heightIn, setHeightIn] = useState('9');
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('25');

  // BMI Calculation
  const calculateBMI = () => {
    let w = parseFloat(weightKg);
    let h = parseFloat(heightCm) / 100; // in meters

    if (unitSystem === 'imperial') {
      w = parseFloat(weightLbs) * 0.453592; // lbs to kg
      let totalInches = (parseFloat(heightFt || 0) * 12) + parseFloat(heightIn || 0);
      h = totalInches * 0.0254; // inches to meters
    }

    if (!w || !h || h === 0) return null;

    let bmi = w / (h * h);
    let score = parseFloat(bmi.toFixed(1));

    let category = '';
    let color = '';
    let bg = '';
    if (score < 18.5) {
      category = 'Underweight';
      color = '#0284c7';
      bg = 'rgba(2, 132, 199, 0.1)';
    } else if (score >= 18.5 && score <= 24.9) {
      category = 'Normal weight';
      color = '#10b981';
      bg = 'rgba(16, 185, 129, 0.1)';
    } else if (score >= 25 && score <= 29.9) {
      category = 'Overweight';
      color = '#f59e0b';
      bg = 'rgba(245, 158, 11, 0.1)';
    } else {
      category = 'Obese';
      color = '#ef4444';
      bg = 'rgba(239, 68, 68, 0.1)';
    }

    // Ideal healthy weight range (BMI 18.5 - 24.9)
    let minWeight = parseFloat((18.5 * (h * h)).toFixed(1));
    let maxWeight = parseFloat((24.9 * (h * h)).toFixed(1));

    let idealRange = `${minWeight} kg - ${maxWeight} kg`;
    if (unitSystem === 'imperial') {
      minWeight = parseFloat((minWeight * 2.20462).toFixed(1));
      maxWeight = parseFloat((maxWeight * 2.20462).toFixed(1));
      idealRange = `${minWeight} lbs - ${maxWeight} lbs`;
    }

    // BMR estimate (Mifflin-St Jeor Equation)
    let bmr = 0;
    let aNum = parseFloat(age || 25);
    if (gender === 'male') {
      bmr = (10 * w) + (6.25 * (h * 100)) - (5 * aNum) + 5;
    } else {
      bmr = (10 * w) + (6.25 * (h * 100)) - (5 * aNum) - 161;
    }
    let bmrScore = Math.round(bmr);

    return { score, category, color, bg, idealRange, bmrScore };
  };

  const bmiData = calculateBMI();

  const handleReset = () => {
    setWeightKg('70');
    setHeightCm('175');
    setWeightLbs('154');
    setHeightFt('5');
    setHeightIn('9');
    setAge('25');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free BMI Calculator - Check Body Mass Index" 
        description="Calculate your Body Mass Index (BMI) instantly. Discover your healthy weight range, weight category, and BMR estimate online."
        keywords="bmi calculator, body mass index check, healthy weight calculator, ideal weight range, bmr estimate, metric imperial bmi"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>BMI Calculator</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Body Mass Index (BMI) Calculator
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Determine your current BMI score, evaluate weight categorization tiers, and calculate your Basal Metabolic Rate (BMR) calorie burn.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Activity size={18} style={{ color: 'var(--primary)' }} /> Health Parameters
            </h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Clear
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '12px' }}>
            <button
              onClick={() => setUnitSystem('metric')}
              style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', background: unitSystem === 'metric' ? 'var(--primary)' : 'transparent', color: unitSystem === 'metric' ? '#ffffff' : 'var(--text-secondary)', transition: 'var(--transition-smooth)' }}
            >
              Metric (kg / cm)
            </button>
            <button
              onClick={() => setUnitSystem('imperial')}
              style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', background: unitSystem === 'imperial' ? 'var(--primary)' : 'transparent', color: unitSystem === 'imperial' ? '#ffffff' : 'var(--text-secondary)', transition: 'var(--transition-smooth)' }}
            >
              Imperial (lbs / ft)
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {unitSystem === 'metric' ? (
              <>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Weight (Kilograms)</label>
                  <input 
                    type="number" 
                    value={weightKg} 
                    onChange={(e) => setWeightKg(e.target.value)}
                    placeholder="e.g. 70"
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Height (Centimeters)</label>
                  <input 
                    type="number" 
                    value={heightCm} 
                    onChange={(e) => setHeightCm(e.target.value)}
                    placeholder="e.g. 175"
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Weight (Pounds)</label>
                  <input 
                    type="number" 
                    value={weightLbs} 
                    onChange={(e) => setWeightLbs(e.target.value)}
                    placeholder="e.g. 154"
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Height (Feet)</label>
                    <input 
                      type="number" 
                      value={heightFt} 
                      onChange={(e) => setHeightFt(e.target.value)}
                      placeholder="e.g. 5"
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Height (Inches)</label>
                    <input 
                      type="number" 
                      value={heightIn} 
                      onChange={(e) => setHeightIn(e.target.value)}
                      placeholder="e.g. 9"
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              </>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Gender</label>
                <select 
                  value={gender} 
                  onChange={(e) => setGender(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Age</label>
                <input 
                  type="number" 
                  value={age} 
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="25"
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                />
              </div>
            </div>

          </div>
        </div>

        {/* Right Side: Dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Heart size={18} style={{ color: '#10b981' }} /> BMI Assessment Dashboard
            </h3>

            {bmiData ? (
              <div>
                {/* Score & Category Display */}
                <div style={{ background: bmiData.bg, padding: '28px', borderRadius: '16px', border: `1px solid ${bmiData.color}`, textAlign: 'center', marginBottom: '24px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: bmiData.color, textTransform: 'uppercase', marginBottom: '6px' }}>Your Body Mass Index Score</div>
                  <div style={{ fontSize: '3.2rem', fontWeight: 900, color: bmiData.color, lineHeight: 1.1, marginBottom: '6px' }}>{bmiData.score}</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)' }}>Category: <span style={{ color: bmiData.color }}>{bmiData.category}</span></div>
                </div>

                {/* Additional Health Metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ background: 'var(--bg-secondary)', padding: '18px', borderRadius: '14px', border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Ideal Weight Range (BMI 18.5 - 24.9)</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)' }}>{bmiData.idealRange}</div>
                  </div>

                  <div style={{ background: 'var(--bg-secondary)', padding: '18px', borderRadius: '14px', border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Basal Metabolic Rate (BMR)</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#f59e0b' }}>{bmiData.bmrScore} kcal / day</div>
                  </div>
                </div>

                {/* WHO Reference Table */}
                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '12px' }}>WHO Weight Classification Reference</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[
                      { range: 'Below 18.5', label: 'Underweight', color: '#0284c7' },
                      { range: '18.5 - 24.9', label: 'Normal healthy weight', color: '#10b981' },
                      { range: '25.0 - 29.9', label: 'Overweight', color: '#f59e0b' },
                      { range: '30.0 and above', label: 'Obese', color: '#ef4444' }
                    ].map(item => (
                      <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{item.label}</span>
                        <span style={{ fontWeight: 700, color: item.color }}>{item.range}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Input weight and height parameters above to calculate BMI.</div>
            )}

            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <AdToast message={toast} />
              <button 
                onClick={withAd('bmi-calc-btn', () => alert("Audit logged client-side."))}
                style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '12px 28px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition-smooth)' }}
              >
                Log Health Audit
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="bmi-calculator" />
    </div>
  );
};

export default BMICalculator;
